import React from 'react'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { fromUnixTime } from 'date-fns/fromUnixTime'

import { useTheme } from '@mui/material/styles'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { Icon, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore } from '../../../common'
import { Device } from '../../../core'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface DeviceListProps {
  devices: Device[]
  onClickDeviceEdit: (device: Device) => void
  onClickDeviceDelete: (device: Device) => void
}

export const DeviceList: React.FC<DeviceListProps> = ({
  devices,
  onClickDeviceEdit,
  onClickDeviceDelete,
}) => {
  const theme = useTheme()
  const { settings: settingsCopy } = useGlobalCopyStore()
  const [expanded, setExpanded] = React.useState<string | false>(false)

  const handleChange = (guid: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? guid : false)
  }

  return (
    <Stack mx={-28} sx={{ '& .MuiAccordion-root': { mb: 0, mt: 0 } }}>
      {devices.map((device) => (
        <Accordion
          expanded={expanded === device.guid}
          key={device.guid}
          onChange={handleChange(device.guid)}
          sx={{ boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<Icon color={'secondary'} name="keyboard_arrow_down" size={24} />}
            sx={{
              padding: '14px 24px',
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
            }}
          >
            <Stack gap={4}>
              <Text bold={true} variant="body1">
                {device.name}
              </Text>
              <Text color={theme.palette.text.secondary} variant="body2">
                {`${device.make} ${device.model}`}
              </Text>
              <Text color={theme.palette.text.secondary} variant="body2">
                {formatDistanceToNow(fromUnixTime(device.created_at), { addSuffix: true })}
              </Text>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            sx={{ backgroundColor: theme.palette.background.default, p: '0 0 0 24px' }}
          >
            <Stack
              onClick={() => onClickDeviceEdit(device)}
              sx={{
                alignItems: 'center',
                flexDirection: 'row',
                width: '100%',
                gap: 12,
                py: 16,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              <IconButton aria-label={settingsCopy.mobile_devices_edit_label} color="secondary">
                <Icon name="edit" />
              </IconButton>
              <Text variant="body1">{settingsCopy.mobile_devices_edit_label}</Text>
            </Stack>
            <Stack
              onClick={() => onClickDeviceDelete(device)}
              sx={{
                alignItems: 'center',
                flexDirection: 'row',
                width: '100%',
                gap: 12,
                py: 16,
                cursor: 'pointer',
              }}
            >
              <IconButton aria-label={settingsCopy.mobile_devices_delete_label} color="secondary">
                <Icon name="delete" />
              </IconButton>
              <Text variant="body1">{settingsCopy.mobile_devices_delete_label}</Text>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
          p: '12px 16px',
        }}
      >
        <Text color={theme.palette.text.secondary} variant="body2">
          {
            buildDynamicCopy(
              settingsCopy.mobile_devices_count,
              <b>{devices.length}</b>,
              <b>{devices.length}</b>,
            ) as string
          }
        </Text>
      </Box>
    </Stack>
  )
}

export default DeviceList
